import { useRouteParams } from '@/contexts/RouteContext';
import { useMutation } from '@apollo/client';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { LimitViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { LimitForm } from './limit-form';
import { buildUpdateLimitInput, type LimitFormSubmitData } from './limit-input';
import { LIMIT_QUERY, LIMIT_UPDATE_MUTATION } from './limits.gql.tsx';

type LimitRecord = LimitViewModel;

export const LimitsEdit = () => {
	const go = useGo();
	const { id } = useRouteParams();
	useEditPageHeader({ id, resourceName: 'limits' });

	// Check if limitId is a valid ULID format (26 characters, base32 encoded)
	const isValidUlid = (id: string): boolean => {
		if (!id || id.length !== 26) return false;
		// ULID uses Crockford's Base32 alphabet: 0123456789ABCDEFGHJKMNPQRSTVWXYZ
		const ulidRegex = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;
		return ulidRegex.test(id.toUpperCase());
	};

	const {
		data: limitResponse,
		isLoading,
		isError,
		error: fetchError
	} = useOne<LimitRecord>({
		resource: 'limits',
		id: id ?? '',
		meta: {
			gqlQuery: LIMIT_QUERY,
			dataPath: 'limit'
		},
		queryOptions: {
			enabled: Boolean(id)
		}
	});

	const limit = limitResponse?.data;

	// Handle invalid ULID case
	if (id && !isValidUlid(id)) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="max-w-md sentry-card">
					<div className="space-y-4 text-center sentry-card-body">
						<div className="text-lg font-medium text-red-500">
							Invalid Limit ID
						</div>
						<p className="text-gray-600">
							The limit ID format is invalid:{' '}
							<code className="px-2 py-1 bg-gray-100 rounded">
								{id}
							</code>
						</p>
						<p className="text-sm text-gray-500">
							Expected a valid ULID (26 character alphanumeric
							string)
						</p>
						<button
							onClick={() => go({ to: '/limits' })}
							className="sentry-btn-primary"
						>
							Back to Limits
						</button>
					</div>
				</div>
			</div>
		);
	}

	const [updateLimit, { loading: isSubmitting }] = useMutation(
		LIMIT_UPDATE_MUTATION
	);

	const handleSubmit = async (formData: LimitFormSubmitData) => {
		if (!limit?.limitId) {
			showMutationErrorToast('limits', 'update', { message: 'Limit identifier is missing.' });
			return;
		}

		const payload = buildUpdateLimitInput(formData, limit.limitId);

		try {
			const { data } = await updateLimit(
				getMutationOptions('limits', { input: payload })
			);

			const message = extractCheckResultError(data?.updateLimit);

			if (message) {
				showMutationErrorToast('limits', 'update', { message });
				return;
			}

			showMutationSuccessToast('limits', 'update');
			// Navigate back with the edited limit ID for selection
			go({
				to: '/limits',
				state: { selectLimitId: limit.limitId }
			});
		} catch (error) {
			showMutationErrorToast('limits', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Limit not found</AlertTitle>
					<AlertDescription>
						A limit identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button
						variant="outline"
						onClick={() => go({ to: '/limits' })}
					>
						← Back
					</Button>
					<h1 className="text-2xl font-bold">Edit Limit</h1>
				</div>

				<Card className="text-sm text-muted-foreground">
					Loading limit details...
				</Card>
			</div>
		);
	}

	if (isError || !limit) {
		return (
			<div className="p-4">
				<Alert variant="destructive">
					<AlertTitle>Unable to load limit</AlertTitle>
					<AlertDescription>
						{fetchError?.message ??
							'The requested limit could not be found.'}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<FormPageLayout>
			<LimitForm
				mode="edit"
				initialData={limit}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Edit Limit"
			/>
		</FormPageLayout>
	);
};
